

import { useEffect, useState } from 'react';

import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { typedYupResolver } from '@eneco/hooks/form';
import { TextLink, Stack, Button, InputSelect, InputNumber } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SmeUsageStepRendering } from '@sitecore/types/manual/smeFlowExperiment/SMEUsageStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Industry } from '../../context';

interface FormFields {
  industryChoice: Industry;
  floorArea: string;
  floorAreaLimit: number;
}

interface AddBusinessDetailsDialogProps {
  handleSubmit: (data: { industryChoice: Industry; floorArea: number }) => void;
}

interface IndustryOptions {
  label: string;
  value: Industry;
}

export const AddBusinessDetailsDialog = ({ handleSubmit }: AddBusinessDetailsDialogProps) => {
  const { SmeUsageStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SmeUsageStep: SmeUsageStepRendering;
  }>();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [industryOptions, setIndustryOptions] = useState<IndustryOptions[]>([]);
  const [industriesValues, setIndustriesValues] = useState<Industry[]>([]);

  useEffect(() => {
    const options: IndustryOptions[] = [];
    const values: Industry[] = [];

    (textualData?.estimationHelp?.industryChoice || []).forEach(industry => {
      options.push({
        label: industry.fields.optionValues.label.value,
        value: industry.fields.optionValues.value.value as Industry,
      });
      values.push(industry.fields.optionValues.value.value as Industry);
    });
    setIndustriesValues(values);
    setIndustryOptions(options);
  }, [textualData]);

  const formSchema = yup.object({
    industryChoice: yup
      .mixed<Industry>()
      .oneOf(industriesValues, textualData?.estimationHelp?.selectListFormField?.value?.requiredMessage),
    floorArea: yup
      .number()
      .typeError(textualData?.estimationHelp?.floorAreaFormField?.value.validationMessage ?? '')
      .required(textualData?.estimationHelp?.floorAreaFormField?.value.requiredMessage ?? '')
      .min(1, textualData?.estimationHelp?.floorAreaFormField?.value.requiredMessage ?? ''),
  });

  const {
    resetField,
    register,
    watch,
    trigger,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormFields>({
    mode: 'onChange',
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const industryChoice = watch('industryChoice');
  const floorArea = watch('floorArea');

  useEffect(() => {
    if (!isOpen) {
      resetField('floorArea');
      resetField('floorAreaLimit');
      resetField('industryChoice');
    }
  }, [isOpen, resetField, setValue]);

  const submit = () => {
    if (!isValid) {
      trigger();
      return;
    }
    setIsLoading(true);

    // Pretend to run the estimation process
    setTimeout(() => {
      setIsOpen(false);
      setIsLoading(false);
      handleSubmit({ industryChoice, floorArea: Number.parseInt(floorArea) });
    }, 500);
  };

  useEffect(() => {
    if (industryChoice && floorArea) {
      trigger('floorArea');
    }
  }, [industryChoice, floorArea, trigger]);

  return (
    <TrackedDialog
      title={textualData?.estimationHelp?.estimationHelpDialog?.value?.title}
      trigger={
        <TextLink onClick={() => setIsOpen(true)} emphasis="high">
          {textualData?.estimationHelp?.estimationHelpDialog?.value?.triggerText}
        </TextLink>
      }
      isOpen={isOpen}
      setOpen={setIsOpen}
      width="regular">
      <Stack direction={'column'} gap="6">
        <InputSelect
          {...register('industryChoice')}
          label={textualData?.estimationHelp?.selectListFormField?.value?.label || ''}
          placeholder={textualData?.estimationHelp?.selectListFormField?.value?.placeholder || ''}
          options={industryOptions}
          error={errors.industryChoice?.message || ''}
        />

        <InputNumber
          {...register('floorArea')}
          formatOptions={{ useGrouping: false }}
          label={textualData?.estimationHelp?.floorAreaFormField?.value?.label || ''}
          placeholder={textualData?.estimationHelp?.floorAreaFormField?.value?.placeholder}
          hint={textualData?.estimationHelp?.floorAreaFormField?.value?.hint}
          error={errors?.floorArea?.message}
        />

        <Stack direction="row" gap="6">
          <Button onClick={submit} isLoading={isLoading}>
            {textualData?.estimationHelp?.estimationHelpDialog?.value?.submitButtonText}
          </Button>
          <TrackedDialog.Close asChild>
            <Button action="secondary">
              {textualData?.estimationHelp?.estimationHelpDialog?.value?.cancelButtonText}
            </Button>
          </TrackedDialog.Close>
        </Stack>
      </Stack>
    </TrackedDialog>
  );
};
